import { readFromDocument, readRawCookieFromDocument } from './cookie';
import {
  type AttributionConsent,
  readAttributionConsentFromDocument,
} from './consent';
import { readGclidFromDocument } from './gclid';

/** Enter 后端识别注册归因的请求头。 */
export const ENTER_GA_CONTEXT_HEADER = 'X-Enter-GA-Context';
/** 当前 Enter GA4 measurement id `G-B92QNZNRMY` 对应的 session cookie。 */
export const ENTER_GA_SESSION_COOKIE_NAME = '_ga_B92QNZNRMY';
/** 单个注册归因 header value 的 UTF-8 字节上限。 */
export const ENTER_GA_CONTEXT_MAX_BYTES = 4 * 1024;

const GA_CLIENT_COOKIE_NAME = '_ga';
const CLIENT_ID_MAX_LENGTH = 128;
const DEFAULT_FIELD_MAX_LENGTH = 512;
type GAConsentValue = 'granted' | 'denied';
const OPTIONAL_CONTEXT_PRUNE_ORDER = [
  'utm_content',
  'utm_term',
  'link_id',
  'utm_campaign',
  'utm_medium',
  'utm_source',
] as const satisfies ReadonlyArray<keyof GARegistrationContext>;

/** GTM readiness 从 gtag 获取、尚未落入 cookie 的浏览器标识。 */
export interface GARegistrationIdentifiers {
  client_id?: string | number;
  session_id?: string | number;
}

/** 本期注册归因仅发送这些字段，未采集的字段不得补默认值。 */
export interface GARegistrationContext {
  analytics_consent: GAConsentValue;
  ad_user_data: GAConsentValue;
  ad_personalization: GAConsentValue;
  client_id?: string;
  session_id?: string;
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  link_id?: string;
}

function normalizeField(
  value: string | number | null | undefined,
  maxLength = DEFAULT_FIELD_MAX_LENGTH,
): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = String(value).trim();
  if (
    !normalized ||
    new TextEncoder().encode(normalized).byteLength > maxLength
  ) {
    return undefined;
  }
  return normalized;
}

/**
 * `_ga` 通常为 `GA1.<n>.<high>.<low>`，client_id 是最后两段。
 * 不绑定中间版本段，避免 GA 调整 cookie scope 时解析失效。
 */
function parseClientId(rawValue: string | null): string | undefined {
  if (!rawValue) return undefined;

  const segments = rawValue.split('.');
  if (segments.length < 4 || !/^GA\d+$/.test(segments[0] ?? '')) {
    return undefined;
  }
  return normalizeField(segments.slice(-2).join('.'), CLIENT_ID_MAX_LENGTH);
}

/**
 * `GS1.1` 的 session_id 是第三个点分段；
 * `GS2.1` 改为 `$` 分隔的标签字段，需要读取 `s<session_id>`。
 */
function parseSessionId(rawValue: string | null): string | undefined {
  if (!rawValue) return undefined;

  const segments = rawValue.split('.');
  const format = segments[0];
  if (segments.length < 3 || !/^GS\d+$/.test(format ?? '')) {
    return undefined;
  }

  if (format === 'GS1') {
    return normalizeField(segments[2]);
  }

  if (format === 'GS2') {
    const sessionField = segments
      .slice(2)
      .join('.')
      .split('$')
      .find((field) => /^s\d+$/.test(field));
    return normalizeField(sessionField?.slice(1));
  }

  return undefined;
}

/** 同步收集首个已登录请求可获得的注册归因；任何 cookie 缺失都只会省略对应字段。 */
export function collectGARegistrationContext(
  consent: AttributionConsent = readAttributionConsentFromDocument(),
  identifiers: GARegistrationIdentifiers = {},
): GARegistrationContext {
  const advertisingConsent: GAConsentValue = consent.advertising
    ? 'granted'
    : 'denied';
  const context: GARegistrationContext = {
    analytics_consent: consent.analytics ? 'granted' : 'denied',
    ad_user_data: advertisingConsent,
    ad_personalization: advertisingConsent,
  };

  if (consent.analytics) {
    const clientId =
      normalizeField(identifiers.client_id, CLIENT_ID_MAX_LENGTH) ??
      parseClientId(readRawCookieFromDocument(GA_CLIENT_COOKIE_NAME));
    const sessionId =
      normalizeField(identifiers.session_id) ??
      parseSessionId(readRawCookieFromDocument(ENTER_GA_SESSION_COOKIE_NAME));
    const utm = readFromDocument();

    if (clientId) context.client_id = clientId;
    if (sessionId) context.session_id = sessionId;

    const utmSource = normalizeField(utm?.utm_source);
    const utmMedium = normalizeField(utm?.utm_medium);
    const utmCampaign = normalizeField(utm?.utm_campaign);
    const utmTerm = normalizeField(utm?.utm_term);
    const utmContent = normalizeField(utm?.utm_content);
    const linkId = normalizeField(utm?.link_id);

    if (utmSource) context.utm_source = utmSource;
    if (utmMedium) context.utm_medium = utmMedium;
    if (utmCampaign) context.utm_campaign = utmCampaign;
    if (utmTerm) context.utm_term = utmTerm;
    if (utmContent) context.utm_content = utmContent;
    if (linkId) context.link_id = linkId;
  }

  if (consent.advertising) {
    const gclid = normalizeField(readGclidFromDocument());
    if (gclid) context.gclid = gclid;
  }

  return context;
}

function encodeBase64Url(bytes: Uint8Array): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let encoded = '';

  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const chunk = (first << 16) | (second << 8) | third;

    encoded += alphabet[(chunk >> 18) & 63];
    encoded += alphabet[(chunk >> 12) & 63];
    encoded += index + 1 < bytes.length ? alphabet[(chunk >> 6) & 63] : '=';
    encoded += index + 2 < bytes.length ? alphabet[chunk & 63] : '=';
  }

  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 将 UTF-8 JSON 编码为无 padding 的 base64url，直接作为 header 值。 */
export function encodeGARegistrationContext(
  context: GARegistrationContext,
): string {
  return encodeBase64Url(new TextEncoder().encode(JSON.stringify(context)));
}

function encodeBoundedGARegistrationContext(
  context: GARegistrationContext,
): string | null {
  const boundedContext = { ...context };
  let encoded = encodeGARegistrationContext(boundedContext);

  for (const field of OPTIONAL_CONTEXT_PRUNE_ORDER) {
    if (
      new TextEncoder().encode(encoded).byteLength <= ENTER_GA_CONTEXT_MAX_BYTES
    ) {
      return encoded;
    }

    delete boundedContext[field];
    encoded = encodeGARegistrationContext(boundedContext);
  }

  return new TextEncoder().encode(encoded).byteLength <=
    ENTER_GA_CONTEXT_MAX_BYTES
    ? encoded
    : null;
}

/**
 * 每次请求即时读取 cookie，保证 Auth0 callback 后的首个已登录请求不会晚于后端建号。
 * 任意浏览器或编码异常均 fail-open，不阻断登录和普通 API 请求。
 */
export function buildGARegistrationRequestHeaders(
  consent: AttributionConsent = readAttributionConsentFromDocument(),
  identifiers: GARegistrationIdentifiers = {},
): Record<string, string> {
  try {
    const context = collectGARegistrationContext(consent, identifiers);

    const encoded = encodeBoundedGARegistrationContext(context);
    return encoded ? { [ENTER_GA_CONTEXT_HEADER]: encoded } : {};
  } catch {
    return {};
  }
}
